import * as THREE from "three";
import { squareToWorldPosition } from "./board";
import type { Square } from "../game/types";

const MARKER_Y = 0.035;

/** Pastilles dorees lumineuses posees sur les cases d'arrivee legales, plus l'anneau de selection. */
export class MoveMarkers {
  private readonly pool: THREE.Mesh[] = [];
  private readonly targetGeometry: THREE.RingGeometry;
  private readonly captureGeometry: THREE.RingGeometry;
  private readonly targetMaterial: THREE.MeshStandardMaterial;
  private readonly captureMaterial: THREE.MeshStandardMaterial;
  private readonly selectionRing: THREE.Mesh;
  private readonly hoverRing: THREE.Mesh;

  constructor(parent: THREE.Group, maxMarkers = 28) {
    const geometry = new THREE.RingGeometry(0.14, 0.22, 24);
    const material = new THREE.MeshStandardMaterial({
      color: "#e8c874",
      emissive: "#c9a349",
      emissiveIntensity: 0.9,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
    });
    // Anneau bien plus large que le socle des pieces (rayon ~0.27, cf.
    // `baseProfile()` dans render/pieces.ts, partage par toutes) : une case de
    // capture est toujours occupee par la piece adverse, un simple disque de la
    // taille du marqueur normal serait entierement cache dessous. Teinte rouge
    // (assortie a --wax-red-bright) pour signaler la prise avant meme de cliquer.
    const captureGeometry = new THREE.RingGeometry(0.3, 0.4, 28);
    const captureMaterial = new THREE.MeshStandardMaterial({
      color: "#e8574f",
      emissive: "#9c2b2b",
      emissiveIntensity: 1,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
    });
    this.targetGeometry = geometry;
    this.captureGeometry = captureGeometry;
    this.targetMaterial = material;
    this.captureMaterial = captureMaterial;
    for (let i = 0; i < maxMarkers; i++) {
      const mesh = new THREE.Mesh(geometry, material);
      mesh.rotation.x = -Math.PI / 2;
      mesh.visible = false;
      parent.add(mesh);
      this.pool.push(mesh);
    }

    const selectionGeometry = new THREE.RingGeometry(0.42, 0.48, 32);
    const selectionMaterial = new THREE.MeshStandardMaterial({
      color: "#ffd9a8",
      emissive: "#ff8a3d",
      emissiveIntensity: 1.1,
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide,
    });
    this.selectionRing = new THREE.Mesh(selectionGeometry, selectionMaterial);
    this.selectionRing.rotation.x = -Math.PI / 2;
    this.selectionRing.visible = false;
    parent.add(this.selectionRing);

    // Anneau plus discret que la selection : indique qu'une piece est survolable
    // (a soi, a son tour) avant meme de cliquer dessus.
    const hoverGeometry = new THREE.RingGeometry(0.34, 0.39, 28);
    const hoverMaterial = new THREE.MeshStandardMaterial({
      color: "#fff3d6",
      emissive: "#ffdf9e",
      emissiveIntensity: 0.6,
      transparent: true,
      opacity: 0.55,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    this.hoverRing = new THREE.Mesh(hoverGeometry, hoverMaterial);
    this.hoverRing.rotation.x = -Math.PI / 2;
    this.hoverRing.visible = false;
    parent.add(this.hoverRing);
  }

  showHover(square: Square | null): void {
    if (square === null) {
      this.hoverRing.visible = false;
      return;
    }
    const pos = squareToWorldPosition(square);
    this.hoverRing.position.set(pos.x, MARKER_Y, pos.z);
    this.hoverRing.visible = true;
  }

  /** `captureSquares` (optionnel) : sous-ensemble de `squares` a marquer en rouge (coup qui prend une piece). */
  showTargets(squares: readonly Square[], captureSquares?: ReadonlySet<Square>): void {
    squares.forEach((square, i) => {
      const mesh = this.pool[i];
      if (!mesh) return;
      const pos = squareToWorldPosition(square);
      mesh.position.set(pos.x, MARKER_Y, pos.z);
      const isCapture = captureSquares?.has(square) ?? false;
      mesh.geometry = isCapture ? this.captureGeometry : this.targetGeometry;
      mesh.material = isCapture ? this.captureMaterial : this.targetMaterial;
      mesh.visible = true;
    });
    for (let i = squares.length; i < this.pool.length; i++) {
      const mesh = this.pool[i];
      if (mesh) mesh.visible = false;
    }
  }

  showSelection(square: Square | null): void {
    if (square === null) {
      this.selectionRing.visible = false;
      return;
    }
    const pos = squareToWorldPosition(square);
    this.selectionRing.position.set(pos.x, MARKER_Y, pos.z);
    this.selectionRing.visible = true;
  }

  hideAll(): void {
    this.pool.forEach((mesh) => (mesh.visible = false));
    this.selectionRing.visible = false;
    this.hoverRing.visible = false;
  }
}
